import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, input, signal, ViewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ControlContainer, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, distinctUntilChanged, Observable, of, switchMap } from 'rxjs';
import { AuthService } from '../../auth/auth.service';
import { UserGraph } from '../models/user-graph.model';

@Component({
    standalone: true,
    selector: 'app-user-graph-autocomplete',
    imports: [
        NgbModule,
        FormsModule,
        CommonModule,
        ReactiveFormsModule,
        MatInputModule,
        MatFormFieldModule,
        MatAutocompleteModule,
    ],
    templateUrl: './user-graph-autocomplete.component.html',
    styleUrls: ['./user-graph-autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,

    // https://stackoverflow.com/a/52926319
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective,
        },
    ],
})
export class UserGraphAutocompleteComponent {
    @ViewChild('userGraphInput') protected userGraphInput!: ElementRef<HTMLInputElement>;

    public controlName = input.required<string>();

    private userNameToSearch = signal(''); // This is updated every time the UserName input's value changes

    protected users$!: Observable<UserGraph[]>;

    public constructor(private readonly authService: AuthService) {
        this.users$ = toObservable(this.userNameToSearch).pipe(
            debounceTime(500),
            distinctUntilChanged(),
            switchMap((value) => {
                if (value === '') {
                    return of([]);
                }

                return this.authService.getAllUsers(value);
            }),
        );
    }

    protected ownerDisplayFn(owner: UserGraph | undefined): string {
        return owner ? owner.displayName : '';
    }

    protected filter(): void {
        const userNameToSearch = this.userGraphInput.nativeElement.value.toLowerCase();
        this.userNameToSearch.set(userNameToSearch);
    }
}
